import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../../services/api';
import { Survey, SurveyResponse, SurveySummaryStats, SurveyResponseItem } from '../../types/survey';
import { useLanguage } from '../../contexts/LanguageContext';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend, Tooltip, ResponsiveContainer } from 'recharts';

// Dimension mapping constants
const DIMENSION_KEYS_JP = [
  'EA目標・スコープ',
  '現行業務・システム',
  'アーキテクチャレビュー',
  'ターゲットアーキテクチャ',
  'アーキテクチャガイド',
  '利用ツール・自動化',
];

const DIMENSION_KEYS_EN = [
  'EA Goals & Scope',
  'Current Systems',
  'Architecture Review',
  'Target Architecture',
  'Architecture Guide',
  'Tools & Automation',
];

// Category to dimension mapping
const CATEGORY_TO_DIMENSION: { [key: string]: string } = {
  // Japanese
  'EA目標': 'EA目標・スコープ',
  'スコープ': 'EA目標・スコープ',
  '期待効果': 'EA目標・スコープ',
  '組織構造': 'EA目標・スコープ',
  '現行業務': '現行業務・システム',
  '現行システム': '現行業務・システム',
  '業務プロセス': '現行業務・システム',
  'システム連携': '現行業務・システム',
  'TCO': '現行業務・システム',
  'アーキテクチャレビュー': 'アーキテクチャレビュー',
  'レビュー': 'アーキテクチャレビュー',
  'ターゲット': 'ターゲットアーキテクチャ',
  '将来像': 'ターゲットアーキテクチャ',
  'ロードマップ': 'ターゲットアーキテクチャ',
  '移行計画': 'ターゲットアーキテクチャ',
  'ガイド': 'アーキテクチャガイド',
  '設計標準': 'アーキテクチャガイド',
  '標準': 'アーキテクチャガイド',
  'ツール': '利用ツール・自動化',
  '自動化': '利用ツール・自動化',
  'EA管理': '利用ツール・自動化',
  // English
  'EA Goals': 'EA Goals & Scope',
  'Scope': 'EA Goals & Scope',
  'Current': 'Current Systems',
  'Systems': 'Current Systems',
  'Review': 'Architecture Review',
  'Target': 'Target Architecture',
  'Guide': 'Architecture Guide',
  'Tools': 'Tools & Automation',
  'Automation': 'Tools & Automation',
};

interface DimensionAnalysis {
  problemStatement: string[];
  keyRisks: string[];
  successCriteria: string[];
}

// Maturity evaluation types and constants
type MaturityLevel = 1 | 2 | 3 | 4 | 5;

interface CategoryScore {
  category: string;
  score: MaturityLevel;
  answeredCount: number;
  totalCount: number;
  avgAnswerLength: number;
  sampleAnswers: string[];
  // LLM-based evaluation fields
  reason?: string;
  strengths?: string[];
  improvements?: string[];
}

// LLM Evaluation result interface
interface LLMEvaluation {
  overallScore: number;
  overallReason: string;
  categoryScores: { [key: string]: { score: number; reason: string; strengths: string[]; improvements: string[] } };
  recommendations: string[];
}

const MATURITY_COLORS: Record<MaturityLevel, string> = {
  1: '#e53e3e',
  2: '#dd6b20',
  3: '#d69e2e',
  4: '#38a169',
  5: '#2b6cb0',
};

const MATURITY_LABELS = {
  ja: {
    1: { label: '初期', desc: '未定義・場当たり的' },
    2: { label: '反復', desc: '部分的に定義' },
    3: { label: '定義済', desc: '標準化・文書化' },
    4: { label: '管理', desc: '測定・改善' },
    5: { label: '最適化', desc: '継続的改善' },
  },
  en: {
    1: { label: 'Initial', desc: 'Ad-hoc' },
    2: { label: 'Repeatable', desc: 'Partially defined' },
    3: { label: 'Defined', desc: 'Standardized' },
    4: { label: 'Managed', desc: 'Measured' },
    5: { label: 'Optimized', desc: 'Continuous improvement' },
  },
};

const SurveyResults: React.FC = () => {
  const { surveyId } = useParams<{ surveyId: string }>();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [results, setResults] = useState<SurveyResponse[]>([]);
  const [summary, setSummary] = useState<SurveySummaryStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'summary' | 'individual'>('summary');
  const [selectedRespondent, setSelectedRespondent] = useState<string | null>(null);
  const [showEvaluation, setShowEvaluation] = useState(false);
  const [showDimensionsMap, setShowDimensionsMap] = useState(false);
  const [dimensionAnalysis, setDimensionAnalysis] = useState<{ [key: string]: DimensionAnalysis } | null>(null);
  const [analyzingDimensions, setAnalyzingDimensions] = useState(false);
  const [dimLang, setDimLang] = useState<'JP' | 'EN'>('JP');
  const [llmEvaluation, setLlmEvaluation] = useState<LLMEvaluation | null>(null);
  const [evaluatingWithLLM, setEvaluatingWithLLM] = useState(false);

  useEffect(() => {
    if (surveyId) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [resultsData, summaryData] = await Promise.all([
        apiClient.getSurveyResults(surveyId!),
        apiClient.getSurveySummary(surveyId!)
      ]);
      setSurvey(resultsData.survey);
      setResults(resultsData.results);
      setSummary(summaryData);
    } catch (err) {
      console.error('Failed to load results:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = (format: 'filled' | 'all' | 'individual', fileType?: 'xlsx' | 'csv') => {
    const url = apiClient.getSurveyExportUrl(surveyId!, format, format === 'filled' ? selectedRespondent || undefined : undefined, fileType);
    window.open(url, '_blank');
  };

  const handleDownloadSourceFile = async () => {
    try {
      const { url } = await apiClient.getSurveySourceFile(surveyId!);
      window.open(url, '_blank');
    } catch (err) {
      console.error('Failed to download source file:', err);
      alert('ソースファイルのダウンロードに失敗しました');
    }
  };

  const handlePdfExport = (format: 'filled' | 'all' | 'individual') => {
    if (!survey || !summary) return;

    let tableHtml = '';
    const titleSuffix = format === 'filled' ? (selectedRespondent || '') : format === 'all' ? '全回答' : '個別回答';

    if (format === 'filled') {
      const targetResult = selectedRespondent ? results.find(r => r.email === selectedRespondent) : null;
      if (!targetResult) return;
      tableHtml = `<h3>${targetResult.respondentName} の回答</h3>
        <table><thead><tr><th>No</th><th>カテゴリ</th><th>質問</th><th>回答</th><th>備考</th></tr></thead><tbody>` +
        (targetResult.responses || []).map(r =>
          `<tr><td>${r.no}</td><td>${r.category}</td><td>${r.question}</td><td>${r.answer || ''}</td><td>${r.notes || ''}</td></tr>`
        ).join('') + '</tbody></table>';
    } else if (format === 'all') {
      const questions = summary.questionStats;
      tableHtml = `<table><thead><tr><th>No</th><th>カテゴリ</th><th>質問</th>` +
        results.map(r => `<th>${r.respondentName}</th>`).join('') +
        `</tr></thead><tbody>` +
        questions.map(q => {
          const cells = results.map(r => {
            const resp = r.responses?.find(resp => resp.no === q.no);
            return `<td>${resp ? (resp.answer || '') : ''}</td>`;
          }).join('');
          return `<tr><td>${q.no}</td><td>${q.category}</td><td>${q.question}</td>${cells}</tr>`;
        }).join('') + '</tbody></table>';
    } else {
      tableHtml = results.map(r =>
        `<h3>${r.respondentName}</h3>
        <table><thead><tr><th>No</th><th>カテゴリ</th><th>質問</th><th>回答</th><th>備考</th></tr></thead><tbody>` +
        (r.responses || []).map(resp =>
          `<tr><td>${resp.no}</td><td>${resp.category}</td><td>${resp.question}</td><td>${resp.answer || ''}</td><td>${resp.notes || ''}</td></tr>`
        ).join('') + '</tbody></table>'
      ).join('<div style="page-break-before:always"></div>');
    }

    const htmlContent = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${survey.title} - ${titleSuffix}</title>
      <style>
        body { font-family: "Hiragino Kaku Gothic ProN", "Yu Gothic", "Meiryo", sans-serif; font-size: 11px; padding: 20px; }
        h2 { color: #2c5282; margin-bottom: 4px; }
        h3 { color: #4a5568; margin: 16px 0 8px; }
        .meta { color: #718096; font-size: 10px; margin-bottom: 16px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
        th, td { border: 1px solid #cbd5e0; padding: 6px 8px; text-align: left; vertical-align: top; word-break: break-word; }
        th { background: #edf2f7; font-weight: 600; white-space: nowrap; }
        tr:nth-child(even) { background: #f7fafc; }
        @media print { body { padding: 0; } .no-print { display: none; } }
      </style></head><body>
      <div class="no-print" style="margin-bottom:16px">
        <button onclick="window.print()" style="padding:8px 20px;background:#2c5282;color:white;border:none;border-radius:4px;cursor:pointer;font-size:13px">PDF保存 / 印刷</button>
      </div>
      <h2>${survey.title}</h2>
      <div class="meta">出力日: ${new Date().toLocaleDateString('ja-JP')} | 形式: ${titleSuffix}</div>
      ${tableHtml}
    </body></html>`;

    const w = window.open('', '_blank');
    if (w) { w.document.write(htmlContent); w.document.close(); }
  };

  // Calculate maturity score for a category based on responses
  const calculateCategoryScore = (items: SurveyResponseItem[]): MaturityLevel => {
    if (items.length === 0) return 1;
    const answered = items.filter(i => i.answer && i.answer.trim().length > 0);
    const completeness = answered.length / items.length;
    const avgLength = answered.length > 0
      ? answered.reduce((sum, i) => sum + (i.answer?.length || 0), 0) / answered.length
      : 0;

    if (completeness >= 0.9 && avgLength >= 80) return 5;
    if (completeness >= 0.7 && avgLength >= 50) return 4;
    if (completeness >= 0.5 && avgLength >= 30) return 3;
    if (completeness >= 0.3 || avgLength >= 15) return 2;
    return 1;
  };

  // Evaluate selected respondent's answers by category
  const evaluation = useMemo(() => {
    if (!selectedRespondent) return null;
    const result = results.find(r => r.email === selectedRespondent);
    if (!result || !result.responses) return null;

    // Group responses by category
    const byCategory: Record<string, SurveyResponseItem[]> = {};
    result.responses.forEach(r => {
      const cat = r.category || 'その他';
      if (!byCategory[cat]) byCategory[cat] = [];
      byCategory[cat].push(r);
    });

    // Calculate scores for each category
    const categoryScores: CategoryScore[] = Object.entries(byCategory).map(([category, items]) => {
      const answered = items.filter(i => i.answer && i.answer.trim().length > 0);
      const avgLength = answered.length > 0
        ? Math.round(answered.reduce((sum, i) => sum + (i.answer?.length || 0), 0) / answered.length)
        : 0;

      return {
        category,
        score: calculateCategoryScore(items),
        answeredCount: answered.length,
        totalCount: items.length,
        avgAnswerLength: avgLength,
        sampleAnswers: answered.slice(0, 2).map(i => i.answer || ''),
      };
    });

    // Calculate overall score
    const overallScore = categoryScores.length > 0
      ? Math.round(categoryScores.reduce((sum, c) => sum + c.score, 0) / categoryScores.length) as MaturityLevel
      : 1;

    return {
      categoryScores,
      overallScore,
      totalAnswered: result.responses.filter(r => r.answer && r.answer.trim()).length,
      totalQuestions: result.responses.length,
    };
  }, [selectedRespondent, results]);

  // Evaluate with LLM
  const evaluateWithLLM = useCallback(async () => {
    if (!selectedRespondent) return;
    const result = results.find(r => r.email === selectedRespondent);
    if (!result || !result.responses) return;

    setEvaluatingWithLLM(true);
    try {
      // Group responses by category
      const categoryAnswers: { [key: string]: Array<{ no: number; question: string; answer: string }> } = {};
      result.responses.forEach(r => {
        const cat = r.category || 'その他';
        if (!categoryAnswers[cat]) categoryAnswers[cat] = [];
        categoryAnswers[cat].push({
          no: r.no,
          question: r.question,
          answer: r.answer || ''
        });
      });

      const llmResult = await apiClient.evaluateMaturity(
        result.respondentName || result.email,
        categoryAnswers,
        language
      );

      if (llmResult.success && llmResult.evaluation) {
        setLlmEvaluation(llmResult.evaluation);
      }
    } catch (err) {
      console.error('Failed to evaluate with LLM:', err);
    } finally {
      setEvaluatingWithLLM(false);
    }
  }, [selectedRespondent, results, language]);

  // Reset LLM evaluation when respondent changes
  useEffect(() => {
    setLlmEvaluation(null);
  }, [selectedRespondent]);

  // Save evaluation to storage
  const handleSaveEvaluation = useCallback(() => {
    if (!llmEvaluation || !selectedRespondent || !survey) return;

    const result = results.find(r => r.email === selectedRespondent);
    const savedEvaluation = {
      surveyId,
      surveyTitle: survey.title,
      respondentEmail: selectedRespondent,
      respondentName: result?.respondentName || selectedRespondent,
      completionRate: result?.completionRate || 0,
      evaluation: llmEvaluation,
      responses: result?.responses || [],
      dimensionAnalysis: dimensionAnalysis || null,
      savedAt: new Date().toISOString(),
    };

    // Save to localStorage
    const existingEvaluations = JSON.parse(localStorage.getItem('savedEvaluations') || '[]');
    const updatedEvaluations = existingEvaluations.filter(
      (e: any) => !(e.surveyId === surveyId && e.respondentEmail === selectedRespondent)
    );
    updatedEvaluations.push(savedEvaluation);
    localStorage.setItem('savedEvaluations', JSON.stringify(updatedEvaluations));

    alert(language === 'ja' ? '評価結果を保存しました（チャート、カテゴリ評価、ディメンション、全回答を含む）' : 'Evaluation saved (including chart, categories, dimensions, all answers)');
  }, [llmEvaluation, selectedRespondent, survey, surveyId, results, language, dimensionAnalysis]);

  // Share evaluation (copy link to clipboard)
  const handleShareEvaluation = useCallback(() => {
    if (!llmEvaluation || !selectedRespondent) return;

    const shareUrl = `${window.location.origin}/admin/surveys/${surveyId}/results?respondent=${encodeURIComponent(selectedRespondent)}`;
    navigator.clipboard.writeText(shareUrl).then(() => {
      alert(language === 'ja' ? '共有リンクをコピーしました' : 'Share link copied to clipboard');
    }).catch(() => {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert(language === 'ja' ? '共有リンクをコピーしました' : 'Share link copied to clipboard');
    });
  }, [llmEvaluation, selectedRespondent, surveyId, language]);

  // Export evaluation as PDF
  const handleExportEvaluationPdf = useCallback(() => {
    if (!llmEvaluation || !selectedRespondent || !survey) return;

    const result = results.find(r => r.email === selectedRespondent);
    const respondentName = result?.respondentName || selectedRespondent;
    const maturityLabel = MATURITY_LABELS[language === 'ja' ? 'ja' : 'en'];

    // Build radar chart data as table
    const radarTableRows = Object.entries(llmEvaluation.categoryScores || {}).map(([cat, data]) =>
      `<tr><td>${cat}</td><td style="text-align:center; font-weight:bold; color:${MATURITY_COLORS[(data.score || 3) as MaturityLevel]}">Lv.${data.score}</td><td>${maturityLabel[(data.score || 3) as MaturityLevel].label}</td></tr>`
    ).join('');

    const categoryHtml = Object.entries(llmEvaluation.categoryScores || {}).map(([cat, data]) => `
      <div style="border: 2px solid ${MATURITY_COLORS[(data.score || 3) as MaturityLevel]}; border-radius: 8px; margin-bottom: 12px; overflow: hidden; page-break-inside: avoid;">
        <div style="background: ${MATURITY_COLORS[(data.score || 3) as MaturityLevel]}; color: white; padding: 8px 12px; display: flex; justify-content: space-between;">
          <strong>${cat}</strong>
          <span>Lv.${data.score}</span>
        </div>
        <div style="padding: 12px; font-size: 12px;">
          <p style="font-style: italic; margin: 0 0 8px;">${data.reason}</p>
          ${data.strengths?.length ? `<div style="margin-bottom: 8px;"><strong style="color: #38a169;">✓ ${language === 'ja' ? '強み' : 'Strengths'}:</strong><ul style="margin: 4px 0 0 16px; padding: 0;">${data.strengths.map(s => `<li>${s}</li>`).join('')}</ul></div>` : ''}
          ${data.improvements?.length ? `<div><strong style="color: #dd6b20;">△ ${language === 'ja' ? '改善点' : 'Improvements'}:</strong><ul style="margin: 4px 0 0 16px; padding: 0;">${data.improvements.map(i => `<li>${i}</li>`).join('')}</ul></div>` : ''}
        </div>
      </div>
    `).join('');

    // Build questions and answers table
    const qaTableRows = result?.responses?.map((r, idx) => `
      <tr style="${idx % 2 === 0 ? 'background:#f9f9f9;' : ''}">
        <td style="padding:8px; border-bottom:1px solid #e2e8f0; font-size:11px;">Q${r.no}</td>
        <td style="padding:8px; border-bottom:1px solid #e2e8f0; font-size:11px; color:#666;">${r.category || ''}</td>
        <td style="padding:8px; border-bottom:1px solid #e2e8f0; font-size:11px;">${r.question}</td>
        <td style="padding:8px; border-bottom:1px solid #e2e8f0; font-size:11px; white-space:pre-wrap;">${r.answer || '<span style="color:#999">未回答</span>'}</td>
      </tr>
    `).join('') || '';

    const htmlContent = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>AI成熟度評価 - ${respondentName}</title>
      <style>
        body { font-family: "Hiragino Kaku Gothic ProN", "Yu Gothic", "Meiryo", sans-serif; font-size: 12px; padding: 24px; max-width: 1000px; margin: 0 auto; }
        h1 { color: #553c9a; font-size: 20px; margin-bottom: 8px; }
        h2 { color: #2d3748; font-size: 16px; margin: 24px 0 12px; border-bottom: 2px solid #805ad5; padding-bottom: 4px; }
        .meta { color: #718096; font-size: 11px; margin-bottom: 24px; }
        .overall { display: flex; align-items: center; gap: 16px; margin-bottom: 24px; padding: 16px; background: #faf5ff; border-radius: 8px; border: 2px solid #805ad5; }
        .score-circle { width: 60px; height: 60px; border-radius: 50%; background: ${MATURITY_COLORS[(llmEvaluation.overallScore || 3) as MaturityLevel]}; color: white; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 16px; flex-shrink: 0; }
        .recommendations { background: #fffbeb; border: 1px solid #f6e05e; border-radius: 8px; padding: 16px; margin-top: 16px; }
        .recommendations h3 { color: #744210; margin: 0 0 12px; }
        .recommendations ol { margin: 0; padding-left: 20px; }
        .radar-table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
        .radar-table th { background: #805ad5; color: white; padding: 10px; text-align: left; }
        .radar-table td { padding: 10px; border-bottom: 1px solid #e2e8f0; }
        .qa-table { width: 100%; border-collapse: collapse; margin-top: 16px; }
        .qa-table th { background: #2c5282; color: white; padding: 10px; text-align: left; font-size: 11px; }
        .legend { display: flex; gap: 12px; margin: 12px 0; flex-wrap: wrap; }
        .legend-item { display: flex; align-items: center; gap: 4px; font-size: 11px; }
        .legend-color { width: 16px; height: 16px; border-radius: 3px; }
        @media print {
          body { padding: 0; }
          .no-print { display: none; }
          .page-break { page-break-before: always; }
        }
      </style></head><body>
      <div class="no-print" style="margin-bottom: 16px; position: sticky; top: 0; background: white; padding: 10px 0; border-bottom: 1px solid #e2e8f0;">
        <button onclick="window.print()" style="padding: 10px 24px; background: #805ad5; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 14px;">📄 PDF保存 / 印刷</button>
        <span style="margin-left: 16px; color: #666; font-size: 12px;">※印刷ダイアログで「PDFとして保存」を選択してください</span>
      </div>

      <h1>🤖 AI成熟度評価レポート</h1>
      <div class="meta">${survey.title} | ${respondentName} | ${new Date().toLocaleDateString('ja-JP')} | 回答率: ${result?.completionRate || 0}%</div>

      <div class="overall">
        <div class="score-circle">Lv.${llmEvaluation.overallScore}</div>
        <div>
          <div style="font-size: 16px; font-weight: bold; color: #553c9a;">総合評価: ${maturityLabel[(llmEvaluation.overallScore || 3) as MaturityLevel].label}</div>
          <div style="color: #4a5568; margin-top: 4px;">${llmEvaluation.overallReason}</div>
        </div>
      </div>

      <h2>📊 成熟度スコアサマリー</h2>
      <div class="legend">
        <div class="legend-item"><div class="legend-color" style="background:#e53e3e"></div> 1: 初期</div>
        <div class="legend-item"><div class="legend-color" style="background:#dd6b20"></div> 2: 反復</div>
        <div class="legend-item"><div class="legend-color" style="background:#d69e2e"></div> 3: 定義済</div>
        <div class="legend-item"><div class="legend-color" style="background:#38a169"></div> 4: 管理</div>
        <div class="legend-item"><div class="legend-color" style="background:#2b6cb0"></div> 5: 最適化</div>
      </div>
      <table class="radar-table">
        <thead><tr><th>カテゴリ</th><th style="width:80px;text-align:center;">スコア</th><th style="width:120px;">レベル</th></tr></thead>
        <tbody>${radarTableRows}</tbody>
      </table>

      <h2>📋 カテゴリ別詳細評価</h2>
      ${categoryHtml}

      ${llmEvaluation.recommendations?.length ? `
        <div class="recommendations">
          <h3>💡 ${language === 'ja' ? '推奨アクション' : 'Recommendations'}</h3>
          <ol>${llmEvaluation.recommendations.map(r => `<li style="margin-bottom: 6px;">${r}</li>`).join('')}</ol>
        </div>
      ` : ''}

      ${dimensionAnalysis ? `
        <div class="page-break"></div>
        <h2>🗺️ ディメンションマッピング</h2>
        <table style="width:100%; border-collapse:collapse; margin-top:12px;">
          <thead>
            <tr style="background:#805ad5; color:white;">
              <th style="padding:10px; text-align:left; border:1px solid #ddd;">ディメンション</th>
              <th style="padding:10px; text-align:left; border:1px solid #ddd;">回答内容</th>
              <th style="padding:10px; text-align:left; border:1px solid #ddd;">キーリスク</th>
              <th style="padding:10px; text-align:left; border:1px solid #ddd;">成功基準</th>
            </tr>
          </thead>
          <tbody>
            ${Object.entries(dimensionAnalysis).map(([dim, data], idx) => `
              <tr style="${idx % 2 === 0 ? 'background:#f9f9f9;' : ''}">
                <td style="padding:10px; border:1px solid #ddd; font-weight:bold; vertical-align:top;">${dim}</td>
                <td style="padding:10px; border:1px solid #ddd; font-size:11px; vertical-align:top;">
                  ${data.problemStatement?.length > 0 ? data.problemStatement.slice(0, 2).map(item => `• ${item}`).join('<br>') : '<span style="color:#999">-</span>'}
                </td>
                <td style="padding:10px; border:1px solid #ddd; font-size:11px; vertical-align:top; color:#c53030;">
                  ${data.keyRisks?.length > 0 ? data.keyRisks.map(item => `⚠ ${item}`).join('<br>') : '<span style="color:#999">-</span>'}
                </td>
                <td style="padding:10px; border:1px solid #ddd; font-size:11px; vertical-align:top; color:#2f855a;">
                  ${data.successCriteria?.length > 0 ? data.successCriteria.map(item => `✓ ${item}`).join('<br>') : '<span style="color:#999">-</span>'}
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      ` : ''}

      <div class="page-break"></div>
      <h2>📝 質問と回答一覧</h2>
      <table class="qa-table">
        <thead><tr><th style="width:40px;">No</th><th style="width:120px;">カテゴリ</th><th style="width:35%;">質問</th><th>回答</th></tr></thead>
        <tbody>${qaTableRows}</tbody>
      </table>
    </body></html>`;

    const w = window.open('', '_blank');
    if (w) { w.document.write(htmlContent); w.document.close(); }
  }, [llmEvaluation, selectedRespondent, survey, results, language, dimensionAnalysis]);

  // Export evaluation as Excel
  const handleExportEvaluationExcel = useCallback(async () => {
    if (!llmEvaluation || !selectedRespondent || !survey) return;

    const result = results.find(r => r.email === selectedRespondent);
    const respondentName = result?.respondentName || selectedRespondent;
    const maturityLabel = MATURITY_LABELS[language === 'ja' ? 'ja' : 'en'];

    // Import xlsx dynamically
    const XLSX = await import('xlsx');

    // Create workbook
    const wb = XLSX.utils.book_new();

    // Summary sheet
    const summaryData = [
      ['AI成熟度評価レポート'],
      [''],
      ['アンケート', survey.title],
      ['回答者', respondentName],
      ['メール', selectedRespondent],
      ['回答率', `${result?.completionRate || 0}%`],
      ['評価日', new Date().toLocaleDateString('ja-JP')],
      [''],
      ['総合評価'],
      ['スコア', `Lv.${llmEvaluation.overallScore}`],
      ['レベル', maturityLabel[(llmEvaluation.overallScore || 3) as MaturityLevel].label],
      ['評価理由', llmEvaluation.overallReason],
      [''],
      ['推奨アクション'],
      ...(llmEvaluation.recommendations || []).map((r, i) => [`${i + 1}. ${r}`]),
    ];
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    // Set column widths
    summarySheet['!cols'] = [{ wch: 20 }, { wch: 80 }];
    XLSX.utils.book_append_sheet(wb, summarySheet, '概要');

    // Category scores sheet
    const categoryData = [
      ['カテゴリ', 'スコア', 'レベル', '評価理由', '強み', '改善点'],
      ...Object.entries(llmEvaluation.categoryScores || {}).map(([cat, data]) => [
        cat,
        data.score,
        maturityLabel[(data.score || 3) as MaturityLevel].label,
        data.reason,
        (data.strengths || []).join('\n'),
        (data.improvements || []).join('\n'),
      ]),
    ];
    const categorySheet = XLSX.utils.aoa_to_sheet(categoryData);
    categorySheet['!cols'] = [{ wch: 25 }, { wch: 8 }, { wch: 12 }, { wch: 40 }, { wch: 30 }, { wch: 30 }];
    XLSX.utils.book_append_sheet(wb, categorySheet, 'カテゴリ評価');

    // Questions and Answers sheet
    const qaData = [
      ['No', 'カテゴリ', '質問', '回答', '備考'],
      ...(result?.responses || []).map(r => [
        r.no,
        r.category || '',
        r.question,
        r.answer || '',
        r.notes || '',
      ]),
    ];
    const qaSheet = XLSX.utils.aoa_to_sheet(qaData);
    qaSheet['!cols'] = [{ wch: 5 }, { wch: 20 }, { wch: 50 }, { wch: 60 }, { wch: 30 }];
    XLSX.utils.book_append_sheet(wb, qaSheet, '質問と回答');

    // Radar Chart Data sheet (for reference)
    const radarData = [
      ['成熟度レーダーチャートデータ'],
      [''],
      ['カテゴリ', '成熟度スコア', '最大スコア'],
      ...Object.entries(llmEvaluation.categoryScores || {}).map(([cat, data]) => [
        cat,
        data.score,
        5,
      ]),
    ];
    const radarSheet = XLSX.utils.aoa_to_sheet(radarData);
    radarSheet['!cols'] = [{ wch: 30 }, { wch: 15 }, { wch: 15 }];
    XLSX.utils.book_append_sheet(wb, radarSheet, 'チャートデータ');

    // Dimensions Mapping sheet (if available)
    if (dimensionAnalysis) {
      const dimData = [
        ['ディメンションマッピング'],
        [''],
        ['ディメンション', '回答内容', 'キーリスク', '成功基準'],
        ...Object.entries(dimensionAnalysis).map(([dim, data]) => [
          dim,
          (data.problemStatement || []).join('\n'),
          (data.keyRisks || []).join('\n'),
          (data.successCriteria || []).join('\n'),
        ]),
      ];
      const dimSheet = XLSX.utils.aoa_to_sheet(dimData);
      dimSheet['!cols'] = [{ wch: 25 }, { wch: 50 }, { wch: 40 }, { wch: 40 }];
      XLSX.utils.book_append_sheet(wb, dimSheet, 'ディメンション');
    }

    // Download
    const filename = `AI評価_${respondentName}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, filename);
  }, [llmEvaluation, selectedRespondent, survey, results, language, dimensionAnalysis]);

  // Map category to dimension
  const mapCategoryToDimension = useCallback((category: string, isJapanese: boolean): string | null => {
    const dims = isJapanese ? DIMENSION_KEYS_JP : DIMENSION_KEYS_EN;
    if (dims.includes(category)) return category;
    for (const [key, dim] of Object.entries(CATEGORY_TO_DIMENSION)) {
      if (category.includes(key)) return dim;
    }
    return null;
  }, []);

  // Build dimension data from selected respondent's answers
  const buildDimensionData = useCallback(() => {
    if (!selectedRespondent) return null;
    const result = results.find(r => r.email === selectedRespondent);
    if (!result || !result.responses) return null;

    const isJapanese = dimLang === 'JP';
    const dims = isJapanese ? DIMENSION_KEYS_JP : DIMENSION_KEYS_EN;
    const data: { [key: string]: DimensionAnalysis } = {};

    dims.forEach(dim => {
      data[dim] = { problemStatement: [], keyRisks: [], successCriteria: [] };
    });

    result.responses.forEach(r => {
      const dim = mapCategoryToDimension(r.category || '', isJapanese);
      if (dim && data[dim] && r.answer && r.answer.trim()) {
        data[dim].problemStatement.push(r.answer.trim());
      }
    });

    return data;
  }, [selectedRespondent, results, dimLang, mapCategoryToDimension]);

  // Analyze dimensions with LLM
  const analyzeDimensionsWithLLM = useCallback(async () => {
    const data = buildDimensionData();
    if (!data) return;

    setAnalyzingDimensions(true);
    try {
      // Start with user's answers
      const analysis: { [key: string]: DimensionAnalysis } = { ...data };

      // Call LLM to generate risks and success criteria
      const dimWithAnswers: { [key: string]: { problemStatement: string[] } } = {};
      Object.entries(data).forEach(([dim, d]) => {
        if (d.problemStatement.length > 0) {
          dimWithAnswers[dim] = { problemStatement: d.problemStatement };
        }
      });

      if (Object.keys(dimWithAnswers).length > 0) {
        const llmResult = await apiClient.analyzeDimensions(dimWithAnswers, dimLang);
        if (llmResult.success && llmResult.results) {
          Object.entries(llmResult.results).forEach(([dim, r]) => {
            if (analysis[dim]) {
              analysis[dim].keyRisks = r.keyRisks || [];
              analysis[dim].successCriteria = r.successCriteria || [];
            }
          });
        }
      }

      setDimensionAnalysis(analysis);
    } catch (err) {
      console.error('Failed to analyze dimensions:', err);
      // Still show user answers even if LLM fails
      setDimensionAnalysis(buildDimensionData());
    } finally {
      setAnalyzingDimensions(false);
    }
  }, [buildDimensionData, dimLang]);

  // Reset dimension analysis when respondent changes
  useEffect(() => {
    setDimensionAnalysis(null);
    setShowDimensionsMap(false);
  }, [selectedRespondent]);

  if (loading) return <div style={{ padding: '30px' }}>{t('common', 'loading')}</div>;
  if (!survey || !summary) return <div style={{ padding: '30px' }}>{t('surveyResults', 'dataNotFound')}</div>;

  const selectedResult = selectedRespondent
    ? results.find(r => r.email === selectedRespondent)
    : null;

  const maturityLabels = MATURITY_LABELS[language === 'ja' ? 'ja' : 'en'];

  return (
    <div style={{ padding: '30px', maxWidth: '1200px', margin: '0 auto' }}>
      <button
        onClick={() => navigate(`/admin/surveys/${surveyId}`)}
        style={{ marginBottom: '16px', padding: '6px 12px', backgroundColor: '#edf2f7', border: 'none', borderRadius: '4px', cursor: 'pointer', color: '#4a5568' }}
      >
        {t('surveyResults', 'backToDetail')}
      </button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <h2 style={{ margin: 0 }}>{survey.title} - {t('surveyResults', 'results')}</h2>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          <button
            onClick={() => navigate(`/admin/surveys/${surveyId}/compare`)}
            style={{ padding: '8px 16px', backgroundColor: '#805ad5', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}
          >
            {t('surveyResults', 'compare')}
          </button>
          <button
            onClick={() => navigate(`/admin/surveys/${surveyId}/dimensions`)}
            style={{ padding: '8px 16px', backgroundColor: '#d69e2e', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}
          >
            Dimensions Map
          </button>
          {/* Excel exports */}
          <button
            onClick={() => handleExport('all')}
            style={{ padding: '8px 16px', backgroundColor: '#38a169', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}
            title="全回答者 Excel"
          >
            Excel (全)
          </button>
          <button
            onClick={() => handleExport('individual')}
            style={{ padding: '8px 16px', backgroundColor: '#2f855a', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}
            title="個別回答 Excel"
          >
            Excel (個別)
          </button>
          {/* CSV exports */}
          <button
            onClick={() => handleExport('all', 'csv')}
            style={{ padding: '8px 16px', backgroundColor: '#2b6cb0', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}
            title="全回答者 CSV"
          >
            CSV (全)
          </button>
          <button
            onClick={() => handleExport('individual', 'csv')}
            style={{ padding: '8px 16px', backgroundColor: '#2c5282', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}
            title="個別回答 CSV"
          >
            CSV (個別)
          </button>
          {/* PDF exports */}
          <button
            onClick={() => handlePdfExport('all')}
            style={{ padding: '8px 16px', backgroundColor: '#c53030', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}
            title="全回答者 PDF"
          >
            PDF (全)
          </button>
          <button
            onClick={() => handlePdfExport('individual')}
            style={{ padding: '8px 16px', backgroundColor: '#9b2c2c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}
            title="個別回答 PDF"
          >
            PDF (個別)
          </button>
          {/* Source file download - only show if survey was imported from Excel */}
          {survey.sourceFile && (
            <button
              onClick={handleDownloadSourceFile}
              style={{ padding: '8px 16px', backgroundColor: '#6b46c1', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}
              title="元のExcelファイルをダウンロード"
            >
              元ファイル
            </button>
          )}
        </div>
      </div>

      {/* Summary stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '24px' }}>
        <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '16px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#2c5282' }}>{summary.totalRespondents}</div>
          <div style={{ fontSize: '0.85rem', color: '#718096' }}>{t('surveyResults', 'totalRespondents')}</div>
        </div>
        <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '16px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#38a169' }}>{summary.completedCount}</div>
          <div style={{ fontSize: '0.85rem', color: '#718096' }}>{t('surveyResults', 'completed')}</div>
        </div>
        <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '16px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#d69e2e' }}>{summary.inProgressCount}</div>
          <div style={{ fontSize: '0.85rem', color: '#718096' }}>{t('surveyResults', 'inProgress')}</div>
        </div>
        <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '16px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#805ad5' }}>{summary.averageCompletionRate}%</div>
          <div style={{ fontSize: '0.85rem', color: '#718096' }}>{t('surveyResults', 'avgCompletionRate')}</div>
        </div>
      </div>

      {/* View mode tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '16px' }}>
        <button
          onClick={() => setViewMode('summary')}
          style={{
            padding: '8px 16px',
            backgroundColor: viewMode === 'summary' ? '#2c5282' : '#edf2f7',
            color: viewMode === 'summary' ? 'white' : '#4a5568',
            border: 'none',
            borderRadius: '4px 4px 0 0',
            cursor: 'pointer'
          }}
        >
          {t('surveyResults', 'summaryTab')}
        </button>
        <button
          onClick={() => setViewMode('individual')}
          style={{
            padding: '8px 16px',
            backgroundColor: viewMode === 'individual' ? '#2c5282' : '#edf2f7',
            color: viewMode === 'individual' ? 'white' : '#4a5568',
            border: 'none',
            borderRadius: '4px 4px 0 0',
            cursor: 'pointer'
          }}
        >
          {t('surveyResults', 'individualTab')}
        </button>
      </div>

      {viewMode === 'summary' ? (
        <div style={{ backgroundColor: 'white', borderRadius: '0 8px 8px 8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 16px' }}>{t('surveyResults', 'questionStats')}</h3>
          {(summary.questionStats || []).map((stat, i) => (
            <div key={i} style={{ padding: '16px', borderBottom: '1px solid #f0f0f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '0.8rem', color: '#718096' }}>Q{stat.no}. {stat.category}</span>
                <span style={{ fontSize: '0.8rem', color: '#718096' }}>{stat.answeredCount}/{stat.totalRespondents}{t('surveyResults', 'answeredCount')}</span>
              </div>
              <p style={{ margin: '0 0 8px', fontWeight: 500 }}>{stat.question}</p>
              {stat.answers.length > 0 && (
                <div style={{ marginTop: '8px' }}>
                  {stat.answers.map((a, j) => (
                    <div key={j} style={{ padding: '6px 10px', backgroundColor: '#f7fafc', borderRadius: '4px', marginBottom: '4px', fontSize: '0.85rem' }}>
                      <span style={{ color: '#2c5282', fontWeight: 500 }}>{a.respondent}:</span>{' '}
                      <span style={{ color: '#4a5568' }}>{a.answer}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ backgroundColor: 'white', borderRadius: '0 8px 8px 8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ marginBottom: '16px' }}>
            <select
              value={selectedRespondent || ''}
              onChange={(e) => setSelectedRespondent(e.target.value || null)}
              style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.9rem' }}
            >
              <option value="">{t('surveyResults', 'selectRespondent')}</option>
              {results.map(r => (
                <option key={r.email} value={r.email}>
                  {r.respondentName} ({r.completionRate}%)
                </option>
              ))}
            </select>
            {selectedRespondent && (
              <span style={{ display: 'inline-flex', gap: '6px', marginLeft: '12px' }}>
                <button
                  onClick={() => handleExport('filled')}
                  style={{ padding: '8px 14px', backgroundColor: '#38a169', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}
                >
                  Excel
                </button>
                <button
                  onClick={() => handleExport('filled', 'csv')}
                  style={{ padding: '8px 14px', backgroundColor: '#2b6cb0', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}
                >
                  CSV
                </button>
                <button
                  onClick={() => handlePdfExport('filled')}
                  style={{ padding: '8px 14px', backgroundColor: '#c53030', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}
                >
                  PDF
                </button>
                <button
                  onClick={() => {
                    setShowEvaluation(!showEvaluation);
                    if (!showEvaluation && !llmEvaluation && !evaluatingWithLLM) {
                      evaluateWithLLM();
                    }
                  }}
                  disabled={evaluatingWithLLM}
                  style={{
                    padding: '8px 14px',
                    backgroundColor: showEvaluation ? '#805ad5' : '#6b46c1',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: evaluatingWithLLM ? 'wait' : 'pointer',
                    fontSize: '0.85rem',
                    fontWeight: showEvaluation ? 700 : 400,
                  }}
                >
                  📊 {evaluatingWithLLM ? (language === 'ja' ? 'AI評価中...' : 'AI Evaluating...') : (language === 'ja' ? 'AI評価' : 'AI Evaluate')}
                </button>
                <button
                  onClick={() => {
                    setShowDimensionsMap(!showDimensionsMap);
                    if (!showDimensionsMap && !dimensionAnalysis) {
                      analyzeDimensionsWithLLM();
                    }
                  }}
                  disabled={analyzingDimensions}
                  style={{
                    padding: '8px 14px',
                    backgroundColor: showDimensionsMap ? '#d69e2e' : '#ecc94b',
                    color: '#1a202c',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: analyzingDimensions ? 'wait' : 'pointer',
                    fontSize: '0.85rem',
                    fontWeight: showDimensionsMap ? 700 : 400,
                  }}
                >
                  🗺️ {analyzingDimensions ? (language === 'ja' ? '分析中...' : 'Analyzing...') : (language === 'ja' ? 'Dimensions Map' : 'Dimensions Map')}
                </button>
              </span>
            )}
          </div>

          {selectedResult ? (
            <div>
              <div style={{ marginBottom: '12px', fontSize: '0.9rem', color: '#718096' }}>
                {t('surveyResults', 'completionRate')} {selectedResult.completionRate}% | {t('surveyResults', 'statusLabel')} {selectedResult.status === 'completed' ? t('surveyResults', 'completed') : t('surveyResults', 'inProgress')}
                {selectedResult.submittedAt && ` | ${t('surveyResults', 'submitted')} ${new Date(selectedResult.submittedAt).toLocaleString('ja-JP')}`}
              </div>

              {/* Evaluation Panel - LLM-based */}
              {showEvaluation && (
                <div style={{
                  marginBottom: '20px',
                  padding: '16px',
                  backgroundColor: '#faf5ff',
                  border: '2px solid #805ad5',
                  borderRadius: '8px',
                }}>
                  {evaluatingWithLLM ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#805ad5' }}>
                      <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🤖</div>
                      <div style={{ fontSize: '1rem', fontWeight: 600 }}>
                        {language === 'ja' ? 'AIが回答内容を分析中...' : 'AI analyzing answers...'}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#718096', marginTop: '8px' }}>
                        {language === 'ja' ? '各カテゴリの成熟度を評価しています' : 'Evaluating maturity for each category'}
                      </div>
                    </div>
                  ) : llmEvaluation ? (
                    <>
                      {/* Overall Score Header - LLM */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                        <div style={{
                          width: '60px',
                          height: '60px',
                          borderRadius: '50%',
                          backgroundColor: MATURITY_COLORS[(llmEvaluation.overallScore || 3) as MaturityLevel],
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 700,
                          fontSize: '1.2rem',
                        }}>
                          Lv.{llmEvaluation.overallScore}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#553c9a' }}>
                            {language === 'ja' ? '🤖 AI成熟度評価' : '🤖 AI Maturity Assessment'}: {maturityLabels[(llmEvaluation.overallScore || 3) as MaturityLevel].label}
                          </div>
                          <div style={{ fontSize: '0.85rem', color: '#4a5568', marginTop: '4px' }}>
                            {llmEvaluation.overallReason}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                          {/* JP/EN Language Switcher */}
                          <div style={{ display: 'flex', border: '1px solid #805ad5', borderRadius: '4px', overflow: 'hidden', marginRight: '8px' }}>
                            <button
                              onClick={() => setDimLang('JP')}
                              style={{
                                padding: '4px 10px',
                                backgroundColor: dimLang === 'JP' ? '#805ad5' : 'transparent',
                                color: dimLang === 'JP' ? 'white' : '#805ad5',
                                border: 'none',
                                cursor: 'pointer',
                                fontWeight: 600,
                                fontSize: '0.7rem',
                              }}
                            >
                              JP
                            </button>
                            <button
                              onClick={() => setDimLang('EN')}
                              style={{
                                padding: '4px 10px',
                                backgroundColor: dimLang === 'EN' ? '#805ad5' : 'transparent',
                                color: dimLang === 'EN' ? 'white' : '#805ad5',
                                border: 'none',
                                cursor: 'pointer',
                                fontWeight: 600,
                                fontSize: '0.7rem',
                              }}
                            >
                              EN
                            </button>
                          </div>
                          <button
                            onClick={() => evaluateWithLLM()}
                            disabled={evaluatingWithLLM}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#805ad5',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                            }}
                          >
                            🔄 {language === 'ja' ? '再評価' : 'Re-evaluate'}
                          </button>
                          <button
                            onClick={() => handleSaveEvaluation()}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#38a169',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                            }}
                          >
                            💾 {language === 'ja' ? '保存' : 'Save'}
                          </button>
                          <button
                            onClick={() => handleShareEvaluation()}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#3182ce',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                            }}
                          >
                            🔗 {language === 'ja' ? '共有' : 'Share'}
                          </button>
                          <button
                            onClick={() => handleExportEvaluationPdf()}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#c53030',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                            }}
                          >
                            📄 PDF
                          </button>
                          <button
                            onClick={() => handleExportEvaluationExcel()}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#2f855a',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                            }}
                          >
                            📊 Excel
                          </button>
                        </div>
                      </div>

                      {/* Radar Chart for Category Scores */}
                      {Object.keys(llmEvaluation.categoryScores || {}).length > 0 && (
                        <div style={{
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          padding: '16px',
                          marginBottom: '16px',
                          border: '1px solid #e2e8f0',
                        }}>
                          <h4 style={{ margin: '0 0 12px', fontSize: '0.95rem', color: '#553c9a', textAlign: 'center' }}>
                            {language === 'ja' ? '成熟度レーダーチャート' : 'Maturity Radar Chart'}
                          </h4>
                          <div style={{ width: '100%', height: '350px' }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <RadarChart
                                data={Object.entries(llmEvaluation.categoryScores || {}).map(([cat, data]) => ({
                                  category: cat.length > 15 ? cat.substring(0, 15) + '...' : cat,
                                  fullName: cat,
                                  score: data.score || 0,
                                  maxScore: 5,
                                }))}
                                margin={{ top: 20, right: 30, bottom: 20, left: 30 }}
                              >
                                <PolarGrid stroke="#e2e8f0" />
                                <PolarAngleAxis
                                  dataKey="category"
                                  tick={{ fill: '#4a5568', fontSize: 11 }}
                                />
                                <PolarRadiusAxis
                                  angle={90}
                                  domain={[0, 5]}
                                  tick={{ fill: '#718096', fontSize: 10 }}
                                  tickCount={6}
                                />
                                <Radar
                                  name={language === 'ja' ? '成熟度スコア' : 'Maturity Score'}
                                  dataKey="score"
                                  stroke="#805ad5"
                                  fill="#805ad5"
                                  fillOpacity={0.5}
                                />
                                <Radar
                                  name={language === 'ja' ? '最大スコア' : 'Max Score'}
                                  dataKey="maxScore"
                                  stroke="#82ca9d"
                                  fill="#82ca9d"
                                  fillOpacity={0.15}
                                />
                                <Tooltip />
                                <Legend wrapperStyle={{ fontSize: '0.8rem' }} />
                              </RadarChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      )}

                      {/* Maturity Legend */}
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px', padding: '8px', backgroundColor: 'white', borderRadius: '4px' }}>
                        {([1, 2, 3, 4, 5] as MaturityLevel[]).map(level => (
                          <div key={level} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.7rem' }}>
                            <div style={{
                              width: '18px',
                              height: '18px',
                              borderRadius: '3px',
                              backgroundColor: MATURITY_COLORS[level],
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 700,
                              fontSize: '0.65rem',
                            }}>
                              {level}
                            </div>
                            <span style={{ color: '#4a5568' }}>{maturityLabels[level].label}</span>
                          </div>
                        ))}
                      </div>

                      {/* Category Scores - LLM */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                        {Object.entries(llmEvaluation.categoryScores || {}).map(([category, catScore], idx) => {
                          const score = (catScore.score || 3) as MaturityLevel;
                          return (
                            <div key={idx} style={{
                              backgroundColor: 'white',
                              borderRadius: '6px',
                              overflow: 'hidden',
                              border: `2px solid ${MATURITY_COLORS[score]}`,
                            }}>
                              <div style={{
                                padding: '8px 12px',
                                backgroundColor: MATURITY_COLORS[score],
                                color: 'white',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                              }}>
                                <span style={{ fontWeight: 600, fontSize: '0.8rem' }}>{category}</span>
                                <span style={{ fontWeight: 700 }}>Lv.{score}</span>
                              </div>
                              <div style={{ padding: '10px 12px', fontSize: '0.78rem', color: '#4a5568' }}>
                                <div style={{ marginBottom: '8px', fontStyle: 'italic' }}>{catScore.reason}</div>
                                {catScore.strengths && catScore.strengths.length > 0 && (
                                  <div style={{ marginBottom: '6px' }}>
                                    <div style={{ fontWeight: 600, color: '#38a169', marginBottom: '2px' }}>
                                      ✓ {language === 'ja' ? '強み' : 'Strengths'}:
                                    </div>
                                    <ul style={{ margin: 0, paddingLeft: '16px' }}>
                                      {catScore.strengths.map((s, i) => <li key={i}>{s}</li>)}
                                    </ul>
                                  </div>
                                )}
                                {catScore.improvements && catScore.improvements.length > 0 && (
                                  <div>
                                    <div style={{ fontWeight: 600, color: '#dd6b20', marginBottom: '2px' }}>
                                      △ {language === 'ja' ? '改善点' : 'Improvements'}:
                                    </div>
                                    <ul style={{ margin: 0, paddingLeft: '16px' }}>
                                      {catScore.improvements.map((imp, i) => <li key={i}>{imp}</li>)}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Recommendations */}
                      {llmEvaluation.recommendations && llmEvaluation.recommendations.length > 0 && (
                        <div style={{ backgroundColor: 'white', borderRadius: '6px', padding: '12px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
                          <div style={{ fontWeight: 700, color: '#553c9a', marginBottom: '8px' }}>
                            💡 {language === 'ja' ? '推奨アクション' : 'Recommendations'}
                          </div>
                          <ol style={{ margin: 0, paddingLeft: '20px', fontSize: '0.85rem', color: '#4a5568' }}>
                            {llmEvaluation.recommendations.map((rec, i) => (
                              <li key={i} style={{ marginBottom: '4px' }}>{rec}</li>
                            ))}
                          </ol>
                        </div>
                      )}

                      {/* Dimensions Mapping based on Answers */}
                      {selectedResult && selectedResult.responses && (
                        <div style={{
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          padding: '16px',
                          border: '1px solid #d69e2e',
                        }}>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '12px',
                          }}>
                            <h4 style={{ margin: 0, fontSize: '0.95rem', color: '#744210' }}>
                              🗺️ {language === 'ja' ? 'ディメンションマッピング（回答に基づく）' : 'Dimension Mapping (Based on Answers)'}
                            </h4>
                            <button
                              onClick={() => {
                                setShowDimensionsMap(!showDimensionsMap);
                                if (!showDimensionsMap && !dimensionAnalysis) {
                                  analyzeDimensionsWithLLM();
                                }
                              }}
                              disabled={analyzingDimensions}
                              style={{
                                padding: '4px 10px',
                                backgroundColor: showDimensionsMap ? '#d69e2e' : '#ecc94b',
                                color: showDimensionsMap ? 'white' : '#744210',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: analyzingDimensions ? 'wait' : 'pointer',
                                fontSize: '0.75rem',
                              }}
                            >
                              {analyzingDimensions ? '分析中...' : (showDimensionsMap ? '閉じる' : '詳細表示')}
                            </button>
                          </div>

                          {/* Quick Dimension Summary */}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '8px', marginBottom: showDimensionsMap ? '12px' : 0 }}>
                            {(dimLang === 'JP' ? DIMENSION_KEYS_JP : DIMENSION_KEYS_EN).map((dim, idx) => {
                              const answersForDim = selectedResult.responses?.filter(r => {
                                const mappedDim = mapCategoryToDimension(r.category || '', dimLang === 'JP');
                                return mappedDim === dim;
                              }) || [];
                              const answeredCount = answersForDim.filter(r => r.answer && r.answer.trim()).length;
                              const totalCount = answersForDim.length;

                              return (
                                <div key={idx} style={{
                                  padding: '8px',
                                  backgroundColor: answeredCount > 0 ? '#f0fff4' : '#f7fafc',
                                  borderRadius: '4px',
                                  border: `1px solid ${answeredCount > 0 ? '#9ae6b4' : '#e2e8f0'}`,
                                  textAlign: 'center',
                                }}>
                                  <div style={{ fontSize: '0.7rem', color: '#4a5568', marginBottom: '4px' }}>
                                    {dim.length > 12 ? dim.substring(0, 12) + '...' : dim}
                                  </div>
                                  <div style={{
                                    fontSize: '1rem',
                                    fontWeight: 700,
                                    color: answeredCount > 0 ? '#38a169' : '#a0aec0',
                                  }}>
                                    {answeredCount}/{totalCount}
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {/* Expanded Dimension Details */}
                          {showDimensionsMap && dimensionAnalysis && (
                            <div style={{ marginTop: '12px', overflowX: 'auto' }}>
                              <table style={{ width: '100%', minWidth: '600px', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                                <thead>
                                  <tr>
                                    <th style={{ padding: '8px', backgroundColor: '#d69e2e', color: 'white', textAlign: 'left', width: '20%' }}>
                                      {language === 'ja' ? 'ディメンション' : 'Dimension'}
                                    </th>
                                    <th style={{ padding: '8px', backgroundColor: '#2c5282', color: 'white', textAlign: 'left', width: '30%' }}>
                                      {language === 'ja' ? '回答内容' : 'Answers'}
                                    </th>
                                    <th style={{ padding: '8px', backgroundColor: '#c53030', color: 'white', textAlign: 'left', width: '25%' }}>
                                      {language === 'ja' ? 'キーリスク' : 'Key Risks'}
                                    </th>
                                    <th style={{ padding: '8px', backgroundColor: '#38a169', color: 'white', textAlign: 'left', width: '25%' }}>
                                      {language === 'ja' ? '成功基準' : 'Success Criteria'}
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(dimLang === 'JP' ? DIMENSION_KEYS_JP : DIMENSION_KEYS_EN).map((dim, idx) => (
                                    <tr key={idx}>
                                      <td style={{ padding: '8px', borderBottom: '1px solid #e2e8f0', fontWeight: 600, color: '#744210', verticalAlign: 'top' }}>
                                        {dim}
                                      </td>
                                      <td style={{ padding: '8px', borderBottom: '1px solid #e2e8f0', backgroundColor: '#f0fff4', verticalAlign: 'top' }}>
                                        {dimensionAnalysis[dim]?.problemStatement?.length > 0 ? (
                                          <ul style={{ margin: 0, paddingLeft: '14px' }}>
                                            {dimensionAnalysis[dim].problemStatement.slice(0, 2).map((item, i) => (
                                              <li key={i} style={{ marginBottom: '4px' }}>
                                                {item.length > 60 ? item.substring(0, 60) + '...' : item}
                                              </li>
                                            ))}
                                          </ul>
                                        ) : (
                                          <span style={{ color: '#a0aec0', fontStyle: 'italic' }}>回答なし</span>
                                        )}
                                      </td>
                                      <td style={{ padding: '8px', borderBottom: '1px solid #e2e8f0', backgroundColor: '#fff5f5', verticalAlign: 'top' }}>
                                        {dimensionAnalysis[dim]?.keyRisks?.length > 0 ? (
                                          <ul style={{ margin: 0, paddingLeft: '14px', color: '#c53030' }}>
                                            {dimensionAnalysis[dim].keyRisks.map((item, i) => (
                                              <li key={i} style={{ marginBottom: '4px' }}>{item}</li>
                                            ))}
                                          </ul>
                                        ) : (
                                          <span style={{ color: '#a0aec0', fontStyle: 'italic' }}>-</span>
                                        )}
                                      </td>
                                      <td style={{ padding: '8px', borderBottom: '1px solid #e2e8f0', backgroundColor: '#f0fff4', verticalAlign: 'top' }}>
                                        {dimensionAnalysis[dim]?.successCriteria?.length > 0 ? (
                                          <ul style={{ margin: 0, paddingLeft: '14px', color: '#276749' }}>
                                            {dimensionAnalysis[dim].successCriteria.map((item, i) => (
                                              <li key={i} style={{ marginBottom: '4px' }}>{item}</li>
                                            ))}
                                          </ul>
                                        ) : (
                                          <span style={{ color: '#a0aec0', fontStyle: 'italic' }}>-</span>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  ) : evaluation ? (
                    <>
                      {/* Fallback to basic evaluation if LLM fails */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                        <div style={{
                          width: '60px',
                          height: '60px',
                          borderRadius: '50%',
                          backgroundColor: MATURITY_COLORS[evaluation.overallScore],
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 700,
                          fontSize: '1.2rem',
                        }}>
                          Lv.{evaluation.overallScore}
                        </div>
                        <div>
                          <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#553c9a' }}>
                            {language === 'ja' ? '総合成熟度評価' : 'Overall Maturity'}: {maturityLabels[evaluation.overallScore].label}
                          </div>
                          <div style={{ fontSize: '0.85rem', color: '#718096' }}>
                            {maturityLabels[evaluation.overallScore].desc} | {evaluation.totalAnswered}/{evaluation.totalQuestions} {language === 'ja' ? '回答済み' : 'answered'}
                          </div>
                        </div>
                        <button
                          onClick={() => evaluateWithLLM()}
                          disabled={evaluatingWithLLM}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#805ad5',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                          }}
                        >
                          🤖 {language === 'ja' ? 'AI評価' : 'AI Evaluate'}
                        </button>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '12px' }}>
                        {evaluation.categoryScores.map((cat, idx) => (
                          <div key={idx} style={{
                            backgroundColor: 'white',
                            borderRadius: '6px',
                            overflow: 'hidden',
                            border: `2px solid ${MATURITY_COLORS[cat.score]}`,
                          }}>
                            <div style={{
                              padding: '8px 12px',
                              backgroundColor: MATURITY_COLORS[cat.score],
                              color: 'white',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}>
                              <span style={{ fontWeight: 600, fontSize: '0.8rem' }}>{cat.category}</span>
                              <span style={{ fontWeight: 700 }}>Lv.{cat.score}</span>
                            </div>
                            <div style={{ padding: '10px 12px', fontSize: '0.78rem', color: '#4a5568' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                <span>{language === 'ja' ? '回答数' : 'Answered'}:</span>
                                <span style={{ fontWeight: 600 }}>{cat.answeredCount}/{cat.totalCount}</span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                <span>{language === 'ja' ? '平均文字数' : 'Avg length'}:</span>
                                <span style={{ fontWeight: 600 }}>{cat.avgAnswerLength}</span>
                              </div>
                              <div style={{ height: '4px', backgroundColor: '#e2e8f0', borderRadius: '2px', overflow: 'hidden' }}>
                                <div style={{
                                  width: `${(cat.answeredCount / cat.totalCount) * 100}%`,
                                  height: '100%',
                                  backgroundColor: MATURITY_COLORS[cat.score],
                                }} />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : null}
                </div>
              )}

              {/* Dimensions Map Panel */}
              {showDimensionsMap && (
                <div style={{
                  marginBottom: '20px',
                  backgroundColor: '#fffbeb',
                  border: '2px solid #d69e2e',
                  borderRadius: '8px',
                  overflow: 'hidden',
                }}>
                  {/* Header */}
                  <div style={{
                    padding: '12px 16px',
                    backgroundColor: '#d69e2e',
                    color: 'white',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                    <span style={{ fontWeight: 700, fontSize: '1rem' }}>
                      🗺️ {dimLang === 'JP' ? 'EA ディメンションマッピング' : 'EA Dimension Mapping'}
                    </span>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      {/* JP/EN Toggle */}
                      <div style={{ display: 'flex', border: '1px solid white', borderRadius: '4px', overflow: 'hidden' }}>
                        <button
                          onClick={() => {
                            setDimLang('JP');
                            setDimensionAnalysis(null);
                            setTimeout(() => analyzeDimensionsWithLLM(), 100);
                          }}
                          style={{
                            padding: '4px 12px',
                            backgroundColor: dimLang === 'JP' ? 'white' : 'transparent',
                            color: dimLang === 'JP' ? '#d69e2e' : 'white',
                            border: 'none',
                            cursor: 'pointer',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                          }}
                        >
                          JP
                        </button>
                        <button
                          onClick={() => {
                            setDimLang('EN');
                            setDimensionAnalysis(null);
                            setTimeout(() => analyzeDimensionsWithLLM(), 100);
                          }}
                          style={{
                            padding: '4px 12px',
                            backgroundColor: dimLang === 'EN' ? 'white' : 'transparent',
                            color: dimLang === 'EN' ? '#d69e2e' : 'white',
                            border: 'none',
                            cursor: 'pointer',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                          }}
                        >
                          EN
                        </button>
                      </div>
                      <button
                        onClick={() => analyzeDimensionsWithLLM()}
                        disabled={analyzingDimensions}
                        style={{
                          padding: '4px 12px',
                          backgroundColor: 'white',
                          color: '#d69e2e',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: analyzingDimensions ? 'wait' : 'pointer',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                        }}
                      >
                        🔄 {dimLang === 'JP' ? '再分析' : 'Re-analyze'}
                      </button>
                    </div>
                  </div>

                  {/* Loading or Table */}
                  {analyzingDimensions ? (
                    <div style={{ padding: '40px', textAlign: 'center', color: '#d69e2e' }}>
                      <div style={{ fontSize: '1.2rem', marginBottom: '8px' }}>⏳</div>
                      {dimLang === 'JP' ? 'LLMで分析中...' : 'Analyzing with LLM...'}
                    </div>
                  ) : dimensionAnalysis ? (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', minWidth: '900px', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            <th style={{
                              width: '80px',
                              padding: '10px 8px',
                              backgroundColor: '#2c3e50',
                              color: 'white',
                              textAlign: 'center',
                              fontSize: '0.75rem',
                              position: 'sticky',
                              left: 0,
                              zIndex: 2,
                            }}>
                              {dimLang === 'JP' ? '分析軸' : 'Analysis'}
                            </th>
                            {(dimLang === 'JP' ? DIMENSION_KEYS_JP : DIMENSION_KEYS_EN).map(dim => (
                              <th key={dim} style={{
                                padding: '10px 8px',
                                backgroundColor: '#3182ce',
                                color: 'white',
                                textAlign: 'center',
                                fontSize: '0.72rem',
                                fontWeight: 600,
                                borderLeft: '1px solid rgba(255,255,255,0.2)',
                                minWidth: '140px',
                              }}>
                                {dim}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {/* 課題定義 Row - User Answers */}
                          <tr>
                            <td style={{
                              padding: '10px 6px',
                              backgroundColor: '#2c5282',
                              color: 'white',
                              fontWeight: 700,
                              fontSize: '0.7rem',
                              textAlign: 'center',
                              writingMode: 'vertical-rl',
                              textOrientation: 'mixed',
                              minHeight: '80px',
                              position: 'sticky',
                              left: 0,
                              zIndex: 1,
                              letterSpacing: dimLang === 'JP' ? '2px' : '0',
                            }}>
                              {dimLang === 'JP' ? '課題定義' : 'Problem'}
                            </td>
                            {(dimLang === 'JP' ? DIMENSION_KEYS_JP : DIMENSION_KEYS_EN).map(dim => (
                              <td key={dim} style={{
                                padding: '8px 10px',
                                borderBottom: '1px solid #e2e8f0',
                                borderLeft: '1px solid #e2e8f0',
                                verticalAlign: 'top',
                                fontSize: '0.72rem',
                                lineHeight: '1.4',
                                color: '#2d3748',
                                backgroundColor: '#f0fff4',
                              }}>
                                {dimensionAnalysis[dim]?.problemStatement?.length > 0 ? (
                                  <ul style={{ margin: 0, paddingLeft: '12px' }}>
                                    {dimensionAnalysis[dim].problemStatement.slice(0, 3).map((item, i) => (
                                      <li key={i} style={{ marginBottom: '4px' }}>
                                        {item.length > 80 ? item.substring(0, 80) + '...' : item}
                                      </li>
                                    ))}
                                  </ul>
                                ) : (
                                  <span style={{ color: '#a0aec0', fontStyle: 'italic' }}>
                                    {dimLang === 'JP' ? '回答なし' : 'No answer'}
                                  </span>
                                )}
                              </td>
                            ))}
                          </tr>
                          {/* キーリスク Row - LLM Generated */}
                          <tr>
                            <td style={{
                              padding: '10px 6px',
                              backgroundColor: '#c53030',
                              color: 'white',
                              fontWeight: 700,
                              fontSize: '0.7rem',
                              textAlign: 'center',
                              writingMode: 'vertical-rl',
                              textOrientation: 'mixed',
                              minHeight: '80px',
                              position: 'sticky',
                              left: 0,
                              zIndex: 1,
                              letterSpacing: dimLang === 'JP' ? '2px' : '0',
                            }}>
                              {dimLang === 'JP' ? 'キーリスク' : 'Key Risks'}
                            </td>
                            {(dimLang === 'JP' ? DIMENSION_KEYS_JP : DIMENSION_KEYS_EN).map(dim => (
                              <td key={dim} style={{
                                padding: '8px 10px',
                                borderBottom: '1px solid #e2e8f0',
                                borderLeft: '1px solid #e2e8f0',
                                verticalAlign: 'top',
                                fontSize: '0.72rem',
                                lineHeight: '1.4',
                                color: '#2d3748',
                                backgroundColor: '#fff5f5',
                              }}>
                                {dimensionAnalysis[dim]?.keyRisks?.length > 0 ? (
                                  <ul style={{ margin: 0, paddingLeft: '12px' }}>
                                    {dimensionAnalysis[dim].keyRisks.map((item, i) => (
                                      <li key={i} style={{ marginBottom: '4px', color: '#c53030' }}>{item}</li>
                                    ))}
                                  </ul>
                                ) : (
                                  <span style={{ color: '#a0aec0', fontStyle: 'italic' }}>
                                    {dimLang === 'JP' ? 'LLM分析待ち' : 'Awaiting LLM'}
                                  </span>
                                )}
                              </td>
                            ))}
                          </tr>
                          {/* 成功基準 Row - LLM Generated */}
                          <tr>
                            <td style={{
                              padding: '10px 6px',
                              backgroundColor: '#38a169',
                              color: 'white',
                              fontWeight: 700,
                              fontSize: '0.7rem',
                              textAlign: 'center',
                              writingMode: 'vertical-rl',
                              textOrientation: 'mixed',
                              minHeight: '80px',
                              position: 'sticky',
                              left: 0,
                              zIndex: 1,
                              letterSpacing: dimLang === 'JP' ? '2px' : '0',
                            }}>
                              {dimLang === 'JP' ? '成功基準' : 'Success'}
                            </td>
                            {(dimLang === 'JP' ? DIMENSION_KEYS_JP : DIMENSION_KEYS_EN).map(dim => (
                              <td key={dim} style={{
                                padding: '8px 10px',
                                borderBottom: '1px solid #e2e8f0',
                                borderLeft: '1px solid #e2e8f0',
                                verticalAlign: 'top',
                                fontSize: '0.72rem',
                                lineHeight: '1.4',
                                color: '#2d3748',
                                backgroundColor: '#f0fff4',
                              }}>
                                {dimensionAnalysis[dim]?.successCriteria?.length > 0 ? (
                                  <ul style={{ margin: 0, paddingLeft: '12px' }}>
                                    {dimensionAnalysis[dim].successCriteria.map((item, i) => (
                                      <li key={i} style={{ marginBottom: '4px', color: '#276749' }}>{item}</li>
                                    ))}
                                  </ul>
                                ) : (
                                  <span style={{ color: '#a0aec0', fontStyle: 'italic' }}>
                                    {dimLang === 'JP' ? 'LLM分析待ち' : 'Awaiting LLM'}
                                  </span>
                                )}
                              </td>
                            ))}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div style={{ padding: '40px', textAlign: 'center', color: '#718096' }}>
                      {dimLang === 'JP' ? '分析を開始してください' : 'Start analysis'}
                    </div>
                  )}
                </div>
              )}

              {(selectedResult.responses || []).map((resp, i) => (
                <div key={i} style={{ padding: '12px', borderBottom: '1px solid #f0f0f0' }}>
                  <div style={{ fontSize: '0.8rem', color: '#718096', marginBottom: '4px' }}>Q{resp.no}. {resp.category}</div>
                  <p style={{ margin: '0 0 6px', fontWeight: 500 }}>{resp.question}</p>
                  <div style={{
                    padding: '8px 12px',
                    backgroundColor: resp.answer ? '#f0fff4' : '#fff5f5',
                    borderRadius: '4px',
                    fontSize: '0.9rem',
                    color: resp.answer ? '#276749' : '#e53e3e'
                  }}>
                    {resp.answer || t('surveyResults', 'unanswered')}
                  </div>
                  {resp.notes && (
                    <div style={{ marginTop: '4px', fontSize: '0.8rem', color: '#718096' }}>{t('surveyResults', 'remarks')} {resp.notes}</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: '#a0aec0' }}>{t('surveyResults', 'selectPrompt')}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default SurveyResults;
